import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import axiosClient from '../../axios'

// ── Types ────────────────────────────────────────────────────────────────

export type CampaignStatus = 'queued' | 'sending' | 'completed' | 'failed'
export type RecipientStatus = 'pending' | 'sent' | 'failed' | 'skipped_unsubscribed'

export interface CampaignRecipient {
  email: string
  status: RecipientStatus
  sentAt: string | null
  error: string | null
}

export interface Campaign {
  id: string
  name: string
  subject: string
  /** Full HTML that was sent — used to prefill "Réexploiter" in the compose form. */
  html: string
  status: CampaignStatus
  totalCount: number
  sentCount: number
  failedCount: number
  skippedCount: number
  recipients: CampaignRecipient[]
  sender: { id: string; contactName: string | null; email: string } | null
  startedAt: string | null
  completedAt: string | null
  createdAt: string
}

export interface CreateCampaignPayload {
  name: string
  subject: string
  html: string
  /** One email per line/comma — the backend splits, validates and dedupes. */
  recipients: string
}

export interface CreateCampaignResponse extends Campaign {
  /** Entries from `recipients` that didn't look like an email — surfaced so the admin can fix typos. */
  invalidRecipients: string[]
}

export interface CampaignsListResponse {
  data: Campaign[]
  meta: { page: number; limit: number; totalItems: number; totalPages: number }
}

// ── Query keys ───────────────────────────────────────────────────────────

const campaignsKey = ['admin', 'campaigns'] as const
const campaignKey = (id: string) => ['admin', 'campaigns', id] as const

// ── Hooks ────────────────────────────────────────────────────────────────

/** Polls every 1.5s as long as some campaign in the list is still sending. */
export function useCampaigns() {
  return useQuery({
    queryKey: campaignsKey,
    queryFn: async () => {
      const { data } = await axiosClient.get<CampaignsListResponse>('/admin/campaigns')
      return data
    },
    staleTime: 15_000,
    refetchInterval: (query) => {
      const active = query.state.data?.data.some((c) => c.status === 'queued' || c.status === 'sending')
      return active ? 1500 : false
    },
  })
}

/** Polls while the campaign is actively sending, stops once it's done. */
export function useCampaign(id: string | null) {
  return useQuery({
    queryKey: id ? campaignKey(id) : ['admin', 'campaigns', 'none'],
    queryFn: async () => {
      const { data } = await axiosClient.get<Campaign>(`/admin/campaigns/${id}`)
      return data
    },
    enabled: !!id,
    refetchInterval: (query) => {
      const status = query.state.data?.status
      return status === 'queued' || status === 'sending' ? 1500 : false
    },
  })
}

export function useCreateCampaign() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (payload: CreateCampaignPayload) => {
      const { data } = await axiosClient.post<CreateCampaignResponse>('/admin/campaigns', payload)
      return data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: campaignsKey })
    },
  })
}

/** Global unsubscribe list — used to flag dead addresses in the compose form before sending. */
export function useCampaignSuppressions() {
  return useQuery({
    queryKey: ['admin', 'campaigns', 'suppressions'],
    queryFn: async () => {
      const { data } = await axiosClient.get<{ emails: string[] }>('/admin/campaigns/suppressions')
      return new Set(data.emails.map((e) => e.toLowerCase()))
    },
    staleTime: 30_000,
  })
}

export function useDeleteCampaign() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await axiosClient.delete(`/admin/campaigns/${id}`)
      return data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: campaignsKey })
    },
  })
}
